/**
 * @ipc/admin-ui · app/router — сборка createBrowserRouter из routeManifest.
 * /login — standalone (вне shell). Всё остальное — дети AppShell (island-layout + гейт сессии);
 * каждый экран лениво грузится (React.lazy) и оборачивается RouteGuard по праву из манифеста.
 * Экран-агент фазы 2 НЕ трогает этот файл — только добавляет запись в route-manifest.ts.
 */
import { Suspense, lazy, type ComponentType } from 'react';
import { createBrowserRouter, Navigate, type RouteObject } from 'react-router-dom';
import { AppShell, RouteGuard } from '@/widgets/app-shell';
import { Skeleton } from '@/shared/ui';
import { routeManifest, type RouteEntry } from './routes/route-manifest';

const LoginPage = lazy(() => import('@/pages/login').then((m) => ({ default: m.Page })));
/** Публичная страница результата оплаты (return_url Multicard) — без auth. */
const PayResultPage = lazy(() => import('@/pages/pay-result').then((m) => ({ default: m.Page })));
/** Публичная страница выбора способа оплаты ссылки — без auth. */
const PayLinkPage = lazy(() => import('@/pages/pay-link').then((m) => ({ default: m.Page })));

function PageFallback() {
  return (
    <div className="flex flex-col gap-3">
      <Skeleton className="h-8 w-48" />
      <Skeleton className="h-40 w-full max-w-md" />
    </div>
  );
}

/** Ленивый компонент экрана из lazy-импорта манифеста (модуль экспортирует Page). */
function lazyElement(entry: RouteEntry): ComponentType {
  return lazy(() => entry.lazy().then((m) => ({ default: m.Page })));
}

function toRouteObject(entry: RouteEntry): RouteObject {
  const Element = lazyElement(entry);
  const element = (
    <RouteGuard permission={entry.permission}>
      <Suspense fallback={<PageFallback />}>
        <Element />
      </Suspense>
    </RouteGuard>
  );
  if (entry.index === true) {
    return { index: true, element };
  }
  // Пути в манифесте абсолютные ('/payments'); для детей AppShell срезаем ведущий слэш.
  return { path: entry.path.replace(/^\//, ''), element };
}

export const router = createBrowserRouter([
  {
    path: '/login',
    element: (
      <Suspense fallback={<PageFallback />}>
        <LoginPage />
      </Suspense>
    ),
  },
  {
    // return_url / return_error_url по умолчанию (без auth, вне AppShell)
    path: '/pay/result',
    element: (
      <Suspense fallback={<PageFallback />}>
        <PayResultPage />
      </Suspense>
    ),
  },
  {
    // Страница выбора способа оплаты (СБП / Payme / карта) — без auth, вне AppShell
    path: '/pay/l/:publicId',
    element: (
      <Suspense fallback={<PageFallback />}>
        <PayLinkPage />
      </Suspense>
    ),
  },
  {
    path: '/',
    element: <AppShell />,
    children: routeManifest.map(toRouteObject),
  },
  {
    path: '*',
    element: <Navigate to="/" replace />,
  },
]);
